<template>
  <div id="collabo" class="bg-contents py-15">
    <v-container>
      <v-row justify="center">
        <v-col cols="12" sm="10" md="8">
          <div class="contents-grid">
            <h2 class="heading text-center mb-12">{{ $t("collabo.heading") }}</h2>
            <div class="grid-container">
              <!-- カード表示 -->
              <div
                v-for="(item, index) in localizedItems"
                :key="index"
                class="content-card"
                @click="openModal(item)"
                :class="{
                  large: isLargeCard(index) && !isMobile,
                  medium: isMediumCard(index) && !isMobile,
                  small: isSmallCard(index) && !isMobile,
                }"
              >
                <v-img :src="item.fullImagePath" cover class="content-image">
                  <div class="title-overlay"></div>
                  <p
                    class="card-title text-caption text-sm-body-2"
                    v-html="item.title"
                  ></p>
                </v-img>
              </div>
            </div>
            <!-- モーダル表示 -->
            <v-dialog v-model="isModalOpen" max-width="600px" class="modal-card">
              <v-btn
                @click="closeModal"
                class="close-btn"
                icon="$close"
                size="large"
                variant="text"
              ></v-btn>
              <v-card>
                <v-img :src="selectedCard?.fullImagePath" cover></v-img>
                <v-card-item>
                  <p v-if="selectedCard?.label" class="modal-label mb-2">
                    {{ selectedCard.label }}
                  </p>
                  <p v-if="selectedCard?.subtitle" class="modal-subtitle mb-2">
                    {{ selectedCard.subtitle }}
                  </p>
                  <h3 class="modal-title">
                    {{ selectedCard?.title }}
                  </h3>
                  <p
                    class="text-body-2 text-sm-body-1 mt-2 text-pre-wrap"
                    v-html="selectedCard?.description"
                  ></p>
                  <p
                    v-if="selectedCard?.price"
                    v-html="selectedCard.price"
                    class="modal-description text-body-2 text-sm-body-1 text-right mt-2"
                  ></p>
                  <div
                    class="button-container my-7"
                    v-if="selectedCard && selectedCard.links"
                  >
                    <v-row class="" justify="center">
                      <v-col
                        v-for="(link, linkIndex) in selectedCard.links"
                        :key="linkIndex"
                        cols="auto"
                      >
                        <v-btn
                          class="agency-button"
                          rounded="xl"
                          size="large"
                          variant="outlined"
                          elevation="0"
                          min-width="120px"
                          style="text-transform: none"
                          :href="link.link"
                          target="_blank"
                          :disabled="link.disabled"
                          :class="link.class"
                        >
                          {{ link.disabled ? t("collabo.sold_out") : link.title }}
                          <!-- <SvgIcon type="mdi" :path="mdiOpenInNew" size="16" /> -->
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                  <p
                    v-if="selectedCard?.note"
                    v-html="selectedCard.note"
                    class="modal-description text-caption text-sm-body-2 text-note text-left mt-2"
                  ></p>
                </v-card-item>
              </v-card>
            </v-dialog>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import localeData, { setOrder, imagePathsMapping, IMAGE_VERSION } from "@/locales/CollaboRation.js";
import { mdiOpenInNew } from "@mdi/js";
import { getBasePath } from '@/utils/basePath';

// 翻訳データの設定
const { t, locale } = useI18n();
const localizedItems = ref([]);
const isModalOpen = ref(false);
const selectedCard = ref(null);

// basePathユーティリティからベースパスを取得
const basePath = computed(() => getBasePath());

// 画像URLを生成する関数（キャッシュ無効化付き）
const generateImageUrl = (imagePath) => {
  // 本番環境では軽量化
  if (!import.meta.env.DEV) {
    return `${basePath.value}${imagePath}?v=${IMAGE_VERSION}`;
  }
  
  // 開発環境では詳細なキャッシュ無効化
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substr(2, 9);
  const sessionId = sessionStorage.getItem('imageSessionId') || Math.random().toString(36).substr(2, 9);
  if (!sessionStorage.getItem('imageSessionId')) {
    sessionStorage.setItem('imageSessionId', sessionId);
  }
  return `${basePath.value}${imagePath}?v=${IMAGE_VERSION}&cb=${timestamp}&t=${randomId}&s=${sessionId}&nocache=1&dev=true`;
};

const updateLocalizedItems = () => {
  const data = localeData[locale.value]?.collabo.items || [];
  const set1 = setOrder['set-1'];
  
  // set-1のcardsの順序に基づいてアイテムをソート
  const sortedItems = set1.cards.map(cardConfig => {
    const item = data.find(item => item.id === cardConfig.id);
    return item ? {
      ...item,
      fullImagePath: generateImageUrl(imagePathsMapping[item.id]),
    } : null;
  }).filter(item => item && item.visible);
  
  localizedItems.value = sortedItems;
  
  // 開発環境でのデバッグ情報
  if (import.meta.env.DEV) {
    console.log('🖼️ CollaboRation 画像URL更新:', sortedItems.map(item => ({
      id: item.id,
      path: item.fullImagePath
    })));
  }
};

const openModal = (item) => {
  selectedCard.value = item;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedCard.value = null;
};

// スマホ判定
const isMobile = window.innerWidth <= 767;

// セットベースのlargeIndicesを使用
const largeIndices = setOrder['set-1'].largeIndices;

// カードのクラスを判定する関数
const isLargeCard = (index) => largeIndices.includes(index);
const isMediumCard = (index) => !isLargeCard(index) && index % 3 === 0;
const isSmallCard = (index) => !isLargeCard(index) && !isMediumCard(index);

// 初期化と監視
updateLocalizedItems();
watch(locale, updateLocalizedItems);
</script>

<style scoped lang="scss">
.contents-grid {
  padding: 16px;
}

.grid-container {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.content-card {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  aspect-ratio: 1 / 1; /* 正方形を維持 */
  @media screen and (max-width: 767px) {
    width: 100%;
    aspect-ratio: 1 / 1; /* 正方形を維持 */
  }
}

.content-image {
  width: 100%;
  height: 100%;
}

.large {
  grid-column: span 2;
  grid-row: span 2;
}

.medium {
  grid-column: span 1;
  grid-row: span 1;
}

.small {
  grid-column: span 1;
  grid-row: span 1;
}

.title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3em;
  mix-blend-mode: multiply;
  z-index: 1;
  &::before {
    position: absolute;
    content: "";
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      0deg,
      rgba(var(--v-theme-primary), 0.9) 0%,
      rgba(var(--v-theme-primary), 0.5) 70%,
      rgba(141, 141, 141, 0)
    );
    z-index: 2;
  }
}

.card-title {
  position: absolute;
  color: white;
  bottom: 0;
  left: 0;
  padding: 16px 8px 4px;
  z-index: 3;
}

.modal-card {
  max-width: 600px;
}

.v-card {
  background-color: rgba(var(--v-theme-background), 1);
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  padding: 0;
  list-style: none;
  &::before {
    content: none;
  }
}

.modal-subtitle {
  color: white;
  font-size: 12px;
  margin: 0;
  padding: 0;
}

.close-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  font-size: 20px;
  color: black;
}

.bg-contents {
  background-color: rgba(var(--v-theme-primary), 0.3);
}

.close-btn {
  position: absolute;
  right: 0;
  top: -50px;
  font-size: 20px;
  color: white;
}

.modal-label {
  color: white;
  background-color: rgba(var(--v-theme-primary), 1);
  font-weight: bold;
  display: inline-block;
  padding: 0.3em 0.5em 0.4em;
  font-size: 12px;
  border-radius: 4px;
}

/* モーダルの背景 */
:deep(.v-overlay__scrim) {
  background: rgb(var(--v-theme-note), 1);
  opacity: 0.7;
}
/* モーダルの高さ設定 */
:deep(.v-overlay__content) {
  max-height: calc(100% - 100px) !important; //上下50pxずつ余白を取る
}
</style>
